import { EstablishmentCard, ProspectSignal } from "../../interfaces/linkedinProspecting";
import { DEFAULT_OFFER, OFFER_TOKEN, firstName } from "./vocabulary";

/**
 * The variables a LinkedIn template can use. Templates keep the tokens; a message shows them
 * coloured while it is edited and they are replaced only when it leaves (`fillVariables`), with
 * what the base knows about this prospect and what the rep set about themselves.
 */
export type VariableGroup = "Prospect" | "Établissement" | "Vous";

export interface VariableDef {
    token: string;
    label: string;
    group: VariableGroup;
    /** Shown where there is no prospect yet (the templates editor). */
    example: string;
}

export const VARIABLES: VariableDef[] = [
    { token: "{firstName}", label: "Prénom", group: "Prospect", example: "Jean" },
    { token: "{lastName}", label: "Nom", group: "Prospect", example: "Dupont" },
    { token: "{name}", label: "Nom complet", group: "Prospect", example: "Jean Dupont" },
    { token: "{title}", label: "Poste LinkedIn", group: "Prospect", example: "Gérant" },
    { token: "{phone}", label: "Téléphone", group: "Prospect", example: "06 12 34 56 78" },
    { token: "{company}", label: "Restaurant", group: "Établissement", example: "Chez Paul" },
    { token: "{type}", label: "Type", group: "Établissement", example: "brasserie" },
    { token: "{location}", label: "Ville", group: "Établissement", example: "Lyon" },
    { token: "{sites}", label: "Nb d'établissements", group: "Établissement", example: "3" },
    { token: "{cities}", label: "Villes du groupe", group: "Établissement", example: "Lyon, Villeurbanne" },
    { token: "{rating}", label: "Note Google", group: "Établissement", example: "4,6" },
    { token: "{reviews}", label: "Avis Google", group: "Établissement", example: "1 250" },
    { token: "{website}", label: "Site web", group: "Établissement", example: "chezpaul.fr" },
    { token: "{signal}", label: "Dernier signal", group: "Établissement", example: "ouverture" },
    { token: OFFER_TOKEN, label: "Votre offre", group: "Vous", example: DEFAULT_OFFER },
    { token: "{mon prénom}", label: "Votre prénom", group: "Vous", example: "Mathieu" },
    { token: "{mon entreprise}", label: "Votre entreprise", group: "Vous", example: "Duckflow" },
    { token: "{mon téléphone}", label: "Votre téléphone", group: "Vous", example: "06 98 76 54 32" },
];

export const VARIABLE_BY_TOKEN = new Map(VARIABLES.map((v) => [v.token, v]));

/** Anything between braces, so an unknown or mistyped variable shows too (in red). */
export const TOKEN_RX = /\{[^{}\n]{1,30}\}/g;

/** token → value; null when the base doesn't know it for this prospect. */
export type VariableValues = Record<string, string | null>;

export interface Me {
    offer: string;
    givenName?: string;
    company?: string;
    phone?: string;
}

const titleCase = (text: string) => text.toLowerCase().replace(/(^|[-\s])(\p{L})/gu, (_, sep, l) => sep + l.toUpperCase());

/** Everything a template can say about this prospect, and about the rep. */
export function variableValues(
    p: {
        name: string;
        headline?: string | null;
        phone?: string | null;
        establishment: EstablishmentCard | null;
        portfolio: { sites: number; cities: string[] };
        signals: ProspectSignal[];
    },
    me: Me
): VariableValues {
    const est = p.establishment;
    const given = firstName(p.name);
    const clean = (v: string | null | undefined) => (v && String(v).trim()) || null;
    return {
        "{firstName}": clean(given),
        "{lastName}": clean(p.name.replace(new RegExp(given, "i"), "").trim()),
        "{name}": clean(p.name),
        "{title}": clean(p.headline),
        "{phone}": clean(p.phone),
        "{company}": clean(est?.name),
        "{type}": clean(est?.type?.replace(/_/g, " ")),
        "{location}": est?.city ? titleCase(est.city) : null,
        "{sites}": p.portfolio.sites ? String(p.portfolio.sites) : null,
        "{cities}": p.portfolio.cities.length ? p.portfolio.cities.slice(0, 3).join(", ") : null,
        "{rating}": typeof est?.rating === "number" ? est.rating.toLocaleString("fr-FR", { maximumFractionDigits: 1 }) : null,
        "{reviews}": typeof est?.reviews === "number" ? est.reviews.toLocaleString("fr-FR") : null,
        "{website}": clean(est?.website?.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "")),
        "{signal}": p.signals[0] ? p.signals[0].label.toLowerCase() : null,
        [OFFER_TOKEN]: me.offer.trim() || DEFAULT_OFFER,
        "{mon prénom}": clean(me.givenName),
        "{mon entreprise}": clean(me.company),
        "{mon téléphone}": clean(me.phone),
    };
}

/** The examples, for editing a template with no prospect at hand. */
export const exampleValues = (offer: string): VariableValues =>
    Object.fromEntries(VARIABLES.map((v) => [v.token, v.token === OFFER_TOKEN ? offer.trim() || v.example : v.example]));

/**
 * The text as it leaves: each known variable replaced by its value. One with no value is dropped
 * with what only made sense around it ("à {location}" goes whole), so the message never reads
 * "Chez Paul à ,". Unknown tokens stay as typed — the editor shows them in red.
 */
export function fillVariables(text: string, values: VariableValues): string {
    let out = text.replace(/\s+à\s+\{location\}/g, (m) => (values["{location}"] ? m : ""));
    out = out.replace(TOKEN_RX, (token) => (token in values ? values[token] || "" : token));
    return out
        .replace(/[ \t]{2,}/g, " ")
        .replace(/ +([,.!?])/g, "$1")
        .trim();
}
